/**
 * InternalMovingFormDialog — bám Access spec
 * `frmFactoryMovementQryInternalMovingAdd` (New/Edit từ
 * `frmFactoryMovementMainSubForm` btnNew/btnEdit, mode Internal Moving).
 *
 * Layout (Access 2-col, Left=2508 trái / Left=9588 phải, theo Top):
 *   Moving Date* (date)     |  Stack Quality (display)
 *   Note*        (memo)     |  Stack Package (display)
 *   Stack ID     (combo)    |  Lot ID        (combo)   ← chọn ĐÚNG 1
 *   Current Weight (lock)   |  Current Bag   (lock)
 *   Current Area  (lock)    |  Moving Weight (lock)
 *   Moving Bag    (lock)    |  Moving Area*  (combo)
 *   Service Provider        |  Service Cost
 *
 * Required (Access `btnSave_Click`): input_date, note, current_area,
 * current_bag, current_weight, moving_area, moving_bag, moving_weight, và
 * ĐÚNG MỘT trong lot_id / stack_id (cả 2 rỗng hoặc cả 2 có → lỗi).
 *
 * Cascade (Access `stack_id_AfterUpdate` / `lot_id_AfterUpdate`): chọn Stack →
 * autofill current_weight/bag/area = balance + moving_weight/bag = balance,
 * moving_area="" , hiện Stack Quality/Package; clear Lot. Chọn Lot → tương tự
 * (không có quality/package); clear Stack. Lookups qua BE
 * `/operations/internal-moving-lookups`.
 *
 * current_* + moving_weight + moving_bag LOCKED (Access Enabled=NotDefault —
 * auto theo balance); chỉ moving_area + note + service_* user nhập tay.
 *
 * Modes: new → empty, input_date = hôm nay; edit → hydrate row (parent merge PK
 * `id`). Không có Copy (Access chỉ New/Edit/Delete).
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import type { FactoryCell, FactoryRowPayload } from '@/store/api/factoryApi'
import {
  useGetInternalMovingCombosQuery,
  useLazyGetInternalMovingLookupsQuery,
} from '@/store/api/factoryApi'
import { FORM_GRID_2COL, HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '@/theme/responsive'

type MovingRow = { id: string | number } & Record<string, FactoryCell>

interface ComboItem {
  value: string
  label: string
}

interface DraftState {
  input_date: string
  stack_id: string
  lot_id: string
  current_weight: string
  current_bag: string
  current_area: string
  moving_weight: string
  moving_bag: string
  moving_area: string
  note: string
  service_provider: string
  service_amount: string
}

const EMPTY_DRAFT: DraftState = {
  input_date: '',
  stack_id: '',
  lot_id: '',
  current_weight: '',
  current_bag: '',
  current_area: '',
  moving_weight: '',
  moving_bag: '',
  moving_area: '',
  note: '',
  service_provider: '',
  service_amount: '',
}

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

const compactInputSx = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}

// Readonly bg standard (skill /convert-form): neutral gray cho Access
// `Enabled = NotDefault` (locked) fields.
const readonlyFieldSx = {
  '& .MuiInputBase-root': {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5',
  },
  '& .MuiInputBase-input': { color: 'text.secondary', fontWeight: 600 },
}

const numericInputProps = {
  style: { textAlign: 'right' as const },
  inputMode: 'decimal' as const,
}

function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Access Format `Standard` → thousand sep, 2 decimals.
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

interface InternalMovingFormDialogProps {
  open: boolean
  /** 'new' = empty + input_date hôm nay; 'edit' = hydrate row (PK `id` parent merge). */
  mode: 'new' | 'edit'
  row: MovingRow | null
  saving: boolean
  factoryCode: string
  /** 4-layer permission guard (memory): dialog disable Save khi false. */
  canEdit?: boolean
  onClose: () => void
  onSave: (payload: FactoryRowPayload) => void
}

export function InternalMovingFormDialog({
  open,
  mode,
  row,
  saving,
  factoryCode,
  canEdit = true,
  onClose,
  onSave,
}: InternalMovingFormDialogProps) {
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  // Stack-only display (Access stack_quality / stack_package DLookup).
  const [stackQuality, setStackQuality] = useState('')
  const [stackPackage, setStackPackage] = useState('')

  const combosQuery = useGetInternalMovingCombosQuery({ factoryCode }, { skip: !open })
  const [triggerLookup] = useLazyGetInternalMovingLookupsQuery()

  const stackOptions = useMemo(() => combosQuery.data?.stack_id ?? [], [combosQuery.data])
  const lotOptions = useMemo(() => combosQuery.data?.lot_id ?? [], [combosQuery.data])
  const areaOptions = useMemo(() => combosQuery.data?.area ?? [], [combosQuery.data])

  /** Hydrate — deps `[open, mode, row]` only (memory: hydrate deps). */
  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && row) {
      setDraft({
        input_date: String(row.input_date ?? '').slice(0, 10),
        stack_id: String(row.stack_id ?? ''),
        lot_id: String(row.lot_id ?? ''),
        current_weight: formatStandard(row.current_weight as number | string | null),
        current_bag: formatStandard(row.current_bag as number | string | null),
        current_area: String(row.current_area ?? ''),
        moving_weight: formatStandard(row.moving_weight as number | string | null),
        moving_bag: formatStandard(row.moving_bag as number | string | null),
        moving_area: String(row.moving_area ?? ''),
        note: String(row.note ?? ''),
        service_provider: String(row.service_provider ?? ''),
        service_amount: formatStandard(row.service_amount as number | string | null),
      })
      // View có sẵn quality_code/package (display) → seed ngay (memory:
      // copy seed readonly from row), không chờ lazy lookup.
      setStackQuality(String(row.quality_code ?? ''))
      setStackPackage(String(row.package ?? ''))
    } else {
      setDraft({ ...EMPTY_DRAFT, input_date: new Date().toISOString().slice(0, 10) })
      setStackQuality('')
      setStackPackage('')
    }
  }, [open, mode, row])

  /** Stack chọn → autofill balances + clear Lot (Access `stack_id_AfterUpdate`). */
  const handleStackChange = async (value: string) => {
    const sid = value.trim()
    setDraft((prev) => ({ ...prev, stack_id: sid, lot_id: '' }))
    if (!sid) {
      setStackQuality('')
      setStackPackage('')
      return
    }
    try {
      const r = await triggerLookup({ factoryCode, stackId: sid }, false).unwrap()
      setStackQuality(r.quality ?? '')
      setStackPackage(r.package ?? '')
      setDraft((prev) => ({
        ...prev,
        stack_id: sid,
        lot_id: '',
        current_weight: formatStandard(r.balance_nw),
        current_bag: formatStandard(r.balance_bag),
        current_area: r.area ?? '',
        moving_weight: formatStandard(r.balance_nw),
        moving_bag: formatStandard(r.balance_bag),
        moving_area: '',
      }))
    } catch {
      // Lookup fail — giữ field cho user; BE raise lúc Save nếu thiếu.
    }
  }

  /** Lot chọn → autofill balances + clear Stack (Access `lot_id_AfterUpdate`). */
  const handleLotChange = async (value: string) => {
    const lid = value.trim()
    setDraft((prev) => ({ ...prev, lot_id: lid, stack_id: '' }))
    setStackQuality('')
    setStackPackage('')
    if (!lid) return
    try {
      const r = await triggerLookup({ factoryCode, lotId: lid }, false).unwrap()
      setDraft((prev) => ({
        ...prev,
        lot_id: lid,
        stack_id: '',
        current_weight: formatStandard(r.balance_nw),
        current_bag: formatStandard(r.balance_bag),
        current_area: r.area ?? '',
        moving_weight: formatStandard(r.balance_nw),
        moving_bag: formatStandard(r.balance_bag),
        moving_area: '',
      }))
    } catch {
      // Lookup fail — giữ field cho user.
    }
  }

  const handleChange = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  /** Inject value đã lưu vào options nếu combo (limit) không chứa (memory:
   *  autocomplete inject current value) → tránh hiện blank trên Edit. */
  const injectOption = (opts: ComboItem[], value: string): ComboItem | null => {
    const v = value.trim()
    if (!v) return null
    return opts.find((o) => o.value === v) ?? { value: v, label: v }
  }
  const selectedStack = useMemo(() => injectOption(stackOptions, draft.stack_id), [stackOptions, draft.stack_id])
  const selectedLot = useMemo(() => injectOption(lotOptions, draft.lot_id), [lotOptions, draft.lot_id])
  const selectedMovingArea = useMemo(
    () => injectOption(areaOptions, draft.moving_area),
    [areaOptions, draft.moving_area],
  )

  // Access `btnSave_Click` validation.
  const dateEmpty = draft.input_date.trim().length === 0
  const noteEmpty = draft.note.trim().length === 0
  const stackFilled = draft.stack_id.trim().length > 0
  const lotFilled = draft.lot_id.trim().length > 0
  // ĐÚNG 1 trong stack/lot (Access: cả 2 rỗng hoặc cả 2 đầy → errCheck=3).
  const lotStackInvalid = stackFilled === lotFilled
  const currentInvalid =
    !draft.current_area.trim() || !draft.current_bag.trim() || !draft.current_weight.trim()
  const movingInvalid =
    !draft.moving_area.trim() || !draft.moving_bag.trim() || !draft.moving_weight.trim()

  const canSave =
    canEdit &&
    !dateEmpty &&
    !noteEmpty &&
    !lotStackInvalid &&
    !currentInvalid &&
    !movingInvalid &&
    !saving

  const buildPayload = (): FactoryRowPayload => {
    const str = (v: string): string | null => {
      const s = v.trim()
      return s.length ? s : null
    }
    return {
      input_date: str(draft.input_date),
      lot_id: str(draft.lot_id),
      stack_id: str(draft.stack_id),
      current_weight: parseNumeric(draft.current_weight),
      moving_weight: parseNumeric(draft.moving_weight),
      current_bag: parseNumeric(draft.current_bag),
      moving_bag: parseNumeric(draft.moving_bag),
      current_area: str(draft.current_area),
      moving_area: str(draft.moving_area),
      note: str(draft.note),
      service_provider: str(draft.service_provider),
      service_amount: parseNumeric(draft.service_amount),
    }
  }

  const handleSave = () => {
    if (!canSave) return
    onSave(buildPayload())
  }

  const titleVerb = mode === 'new' ? 'New' : 'Edit'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        onClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { maxHeight: 'min(760px, 92vh)', width: '100%', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ pb: 1, pt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Typography
          component="span"
          noWrap
          title={`${titleVerb} — Factory · Internal Moving`}
          sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {titleVerb} —
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            {' Factory ·'}
          </Box>
          {' Internal Moving'}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          {factoryCode.toUpperCase()}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={handleSave}
            disabled={!canSave}
            sx={titleBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={onClose} disabled={saving} sx={titleBtnSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={{ ...compactInputSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0, pt: 1.5 }}>
        {lotStackInvalid ? (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.25 }}>
            Pick exactly one source — either a Stack ID or a Lot ID (not both).
          </Alert>
        ) : null}

        <Box sx={FORM_GRID_2COL}>
          {/* Row 1 — Moving Date* | Stack Quality (display) */}
          <DatePicker
            label="Moving Date"
            format="DD-MMM-YYYY"
            value={draft.input_date ? dayjs(draft.input_date) : null}
            onChange={(d) => handleChange('input_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                required: true,
                InputLabelProps: { shrink: true },
                sx: { gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 1 } },
              },
            }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 1 }, ...readonlyFieldSx }}
            label="Stack Quality"
            size="small"
            fullWidth
            value={stackQuality}
            InputProps={{ readOnly: true }}
          />

          {/* Row 2 — Note* | Stack Package (display) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 2 } }}
            label="Note"
            size="small"
            fullWidth
            required
            error={noteEmpty}
            value={draft.note}
            onChange={(e) => handleChange('note', e.target.value)}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 2 }, ...readonlyFieldSx }}
            label="Stack Package"
            size="small"
            fullWidth
            value={stackPackage}
            InputProps={{ readOnly: true }}
          />

          {/* Row 3 — Stack ID (combo) | Lot ID (combo) — chọn ĐÚNG 1 */}
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 3 } }}
            size="small"
            options={stackOptions}
            loading={combosQuery.isFetching}
            value={selectedStack}
            getOptionLabel={(o) => (o.value === o.label || !o.label ? o.value : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => void handleStackChange(v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Stack ID" fullWidth helperText="Stack or Lot — pick one." />
            )}
          />
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 3 } }}
            size="small"
            options={lotOptions}
            loading={combosQuery.isFetching}
            value={selectedLot}
            getOptionLabel={(o) => (o.value === o.label || !o.label ? o.value : `${o.value} — ${o.label}`)}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => void handleLotChange(v?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Lot ID" fullWidth />}
          />

          {/* Row 4 — Current Weight (lock) | Current Bag (lock) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 4 }, ...readonlyFieldSx }}
            label="Current Weight"
            size="small"
            fullWidth
            value={draft.current_weight}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 4 }, ...readonlyFieldSx }}
            label="Current Bag"
            size="small"
            fullWidth
            value={draft.current_bag}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />

          {/* Row 5 — Current Area (lock) | Moving Weight (lock) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 5 }, ...readonlyFieldSx }}
            label="Current Area"
            size="small"
            fullWidth
            value={draft.current_area}
            InputProps={{ readOnly: true }}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 5 }, ...readonlyFieldSx }}
            label="Moving Weight"
            size="small"
            fullWidth
            value={draft.moving_weight}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />

          {/* Row 6 — Moving Bag (lock) | Moving Area* (combo) */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 6 }, ...readonlyFieldSx }}
            label="Moving Bag"
            size="small"
            fullWidth
            value={draft.moving_bag}
            inputProps={numericInputProps}
            InputProps={{ readOnly: true }}
          />
          <Autocomplete<ComboItem, false, false, false>
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 6 } }}
            size="small"
            options={areaOptions}
            loading={combosQuery.isFetching}
            value={selectedMovingArea}
            getOptionLabel={(o) => o.label || o.value}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            onChange={(_, v) => handleChange('moving_area', v?.value ?? '')}
            renderInput={(params) => (
              <TextField {...params} label="Moving Area" required error={!draft.moving_area.trim()} fullWidth />
            )}
          />

          {/* Row 7 — Service Provider | Service Cost */}
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 1 }, gridRow: { sm: 7 } }}
            label="Service Provider"
            size="small"
            fullWidth
            value={draft.service_provider}
            onChange={(e) => handleChange('service_provider', e.target.value)}
          />
          <TextField
            sx={{ gridColumn: { xs: 1, sm: 2 }, gridRow: { sm: 7 } }}
            label="Service Cost"
            size="small"
            fullWidth
            type="text"
            value={draft.service_amount}
            onChange={(e) => handleChange('service_amount', e.target.value)}
            onBlur={() => {
              const f = formatStandard(draft.service_amount)
              if (f !== draft.service_amount) handleChange('service_amount', f)
            }}
            inputProps={numericInputProps}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default InternalMovingFormDialog
